import { useAuth } from './context';
import { useServicesQuery } from '@/services/api';
import { getUserDisplayName } from './user-display-name';

export interface TeammateProfile {
  id: number;
  name: string;
  email: string;
  avatarUrl: string | null;
}

/**
 * Fetches the account's user list and returns a teammate's profile by ID.
 *
 * The user list is cached for the lifetime of the session (staleTime: Infinity).
 * Returns null while loading or if the user is not found.
 *
 * For deleted users, returns a placeholder with name "Deleted User".
 */
export function useTeammateProfile(id: number | null | undefined): TeammateProfile | null {
  const { account } = useAuth();

  const { data: users } = useServicesQuery(
    'getAllUsers',
    { params: { account_id: account.id } },
    { staleTime: Infinity, gcTime: Infinity },
  );

  if (id == null || id === 0 || users == null) {
    return null;
  }

  const user = users.users.find((u) => u.user_id === id);

  if (user == null || user.deleted) {
    return {
      id,
      name: 'Deleted User',
      email: '',
      avatarUrl: null,
    };
  }

  return {
    id: user.user_id,
    name: getUserDisplayName(user),
    email: user.email,
    avatarUrl: null,
  };
}
